import { useContext } from "react";

import {
  BookmarkSVG,
  CrownMiniOutlineSVG,
  HistorySVG,
  ListSVG,
  LogoutSVG,
  TriangleDownSVG,
  UserSVG,
} from "../../../svg";
import { NavbarContext } from "../navbarContext";

import styles from "./accountMenu.module.scss";
import { UserContext } from "../../../features/user/userContext";

import { FormattedMessage } from "react-intl";
import { useFreeTrialDuration } from "../../../features/user/selectors";
import { getSignInUrl, getSignUpUrl } from "../../../helpers/routingHelpers";
import { getUpsellRedirectUrl } from "../../../helpers/upsellHelpers";

function AccountMenuMobileButton({ withClass }) {
  const navContext = useContext(NavbarContext);
  const user = useContext(UserContext);

  return (
    <div className={`${styles.menuLocator} ${styles.menuLocatorMobile}`}>
      <button
        className={`${withClass} ${styles.accountMobileButton} ${
          navContext.account_menu_is_visible ? styles.accountButtonActive : ""
        }`}
        onClick={navContext.toggleAccountMenu}
      >
        {user ? (
          <div className={styles.withAvatar}>
            <img
              src={user.avatarSrc}
              width={25}
              height={25}
              style={{ borderRadius: "50%" }}
            />
            <TriangleDownSVG />
          </div>
        ) : (
          <UserSVG />
        )}
      </button>
      <AccountMenu />
    </div>
  );
}
function AccountMenuActionBarButton({ withClass }) {
  const navContext = useContext(NavbarContext);
  const user = useContext(UserContext);

  return (
    <div className={styles.menuLocator}>
      <button
        className={`${withClass || ""} ${styles.userMenu} ${
          navContext.account_menu_is_visible ? styles.accountButtonActive : ""
        }`}
        onClick={navContext.toggleAccountMenu}
      >
        {user ? (
          <div className={styles.withAvatar}>
            <img
              src={user.avatarSrc}
              width={25}
              height={25}
              style={{ borderRadius: "50%" }}
            />
            <TriangleDownSVG />
          </div>
        ) : (
          <UserSVG />
        )}
      </button>
      <AccountMenu />
    </div>
  );
}

function AccountMenu() {
  const navContext = useContext(NavbarContext);
  const freeTrialDuration = useFreeTrialDuration();
  const user = useContext(UserContext);

  return (
    <div
      className={`${styles.accountMenu} ${
        navContext.account_menu_is_visible ? styles.accountMenuOpen : ""
      }`}
    >
      {user ? (
        <UserView
          user_name={user.username}
          user_thumbnail={user.avatarSrc}
          isPremium={user?.isPremium}
          isEligible={user?.isEligible}
          freeTrialDuration={freeTrialDuration}
          toggleAccountMenu={navContext.toggleAccountMenu}
        />
      ) : (
        <DefaultView freeTrialDuration={freeTrialDuration} />
      )}
    </div>
  );
}

function DefaultView({ freeTrialDuration }) {
  return (
    <ul className={styles.defaultView}>
      <li>
        <a href={getSignUpUrl()} className={styles.accountLink}>
          <div>
            <h5>
              <FormattedMessage id="menu.signup_title" />
            </h5>
            <p>
              <FormattedMessage id="menu.signup_description" />
            </p>
          </div>
        </a>
      </li>
      <li>
        <a href={getSignInUrl()} className={styles.accountLink}>
          <div>
            <h5>
              <FormattedMessage id="menu.signin_title" />
            </h5>
            <p>
              <FormattedMessage id="menu.signin_description" />
            </p>
          </div>
        </a>
      </li>
      <li>
        <a
          href={getUpsellRedirectUrl("newweb_organic_acct_menu_news")}
          className={styles.premiumButton}
        >
          <CrownMiniOutlineSVG />
          <FormattedMessage
            id="menu.upsell.try_free"
            values={{ freeTrialDuration }}
          />
        </a>
      </li>
    </ul>
  );
}

function UserView({
  user_name,
  user_thumbnail,
  freeTrialDuration,
  isPremium,
  isEligible,
}) {
  return (
    <div className={styles.loggedInView}>
      <div className={styles.userDetailBox}>
        <img src={user_thumbnail} width={40} height={40} />
        <h5>{user_name}</h5>
      </div>
      <ul className={styles.userDetailsList}>
        {!isPremium && (
          <li>
            <a
              href={getUpsellRedirectUrl("newweb_organic_acct_menu_news")}
              className={styles.premiumButton}
            >
              <CrownMiniOutlineSVG />
              {isEligible ? (
                <FormattedMessage
                  id="menu.upsell.try_free"
                  values={{ freeTrialDuration }}
                />
              ) : (
                <FormattedMessage id="menu.upsell.premium" />
              )}
            </a>
          </li>
        )}
        <li>
          <a
            href="https://www.crunchyroll.com/watchlist"
            className={`${styles.accountLink} ${styles.withIcon}`}
          >
            <div className={styles.icon}>
              <BookmarkSVG />
            </div>
            <div>
              <h5>
                <FormattedMessage id="menu.watchlist" />
              </h5>
            </div>
          </a>
        </li>
        <li>
          <a
            href="https://www.crunchyroll.com/crunchylists"
            className={`${styles.accountLink} ${styles.withIcon}`}
          >
            <div className={styles.icon}>
              <ListSVG />
            </div>
            <div>
              <h5>
                <FormattedMessage id="menu.crunchylists" />
              </h5>
            </div>
          </a>
        </li>
        <li>
          <a
            href="https://www.crunchyroll.com/history"
            className={`${styles.accountLink} ${styles.withIcon}`}
          >
            <div className={styles.icon}>
              <HistorySVG />
            </div>
            <div>
              <h5>
                <FormattedMessage id="menu.history" />
              </h5>
            </div>
          </a>
        </li>
      </ul>
      <ul className={styles.userAccountList}>
        <li>
          <a
            href="https://www.crunchyroll.com/account/preferences"
            className={`${styles.accountLink} ${styles.withIconStroke}`}
          >
            <div className={`${styles.icon} ${styles.userIcon}`}>
              <UserSVG width={16} height={20} />
            </div>
            <div>
              <h5>
                <FormattedMessage id="menu.account" />
              </h5>
              <p>
                <FormattedMessage id="menu.account_description" />
              </p>
            </div>
          </a>
        </li>
      </ul>
      <ul className={styles.userLogoutList}>
        <li>
          <a
            href="https://www.crunchyroll.com/logout"
            className={`${styles.accountLink} ${styles.withIcon}`}
          >
            <div className={styles.icon}>
              <LogoutSVG />
            </div>
            <div>
              <h5>
                <FormattedMessage id="menu.log_out_title" />
              </h5>
            </div>
          </a>
        </li>
      </ul>
    </div>
  );
}

export { AccountMenu, AccountMenuMobileButton, AccountMenuActionBarButton };
